import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Users, BarChart3, Globe, Activity, Search, RefreshCw,
    ShieldCheck, AlertTriangle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const fetchStats = async () => {
        try {
            const { data } = await axios.get(`${API}/admin/stats`, { withCredentials: true });
            setStats(data);
        } catch (error) {
            toast.error('Failed to load admin stats');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, []);

    if (isLoading) {
        return <div className="flex items-center justify-center h-96 text-indigo-600">Loading Dashboard...</div>;
    }

    return (
        <div className="space-y-6" data-testid="admin-dashboard">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Admin Dashboard
                </h2>
                <Button onClick={fetchStats} variant="outline" size="sm">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <StatCard icon={<Users className="text-blue-500" />} label="Total Users" value={stats?.total_users} />
                <StatCard icon={<Activity className="text-green-500" />} label="Total Orders" value={stats?.total_orders} />
                <StatCard icon={<Globe className="text-purple-500" />} label="Active Proxies" value={stats?.active_proxies} />
                <StatCard icon={<ShieldCheck className="text-orange-500" />} label="Total Proxies" value={stats?.total_proxies} />
            </div>

            {/* Sub-Tabs */}
            <Tabs defaultValue="users" className="space-y-6">
                <TabsList className="bg-white/80 p-1 border border-gray-200 w-full justify-start">
                    <TabsTrigger value="users">User Management</TabsTrigger>
                    <TabsTrigger value="strategies">Strategies</TabsTrigger>
                    <TabsTrigger value="orders">Global Orders</TabsTrigger>
                    <TabsTrigger value="proxies">Proxy Pool</TabsTrigger>
                </TabsList>

                <TabsContent value="users">
                    <UsersTable />
                </TabsContent>

                <TabsContent value="strategies">
                    <StrategiesTable />
                </TabsContent>

                <TabsContent value="orders">
                    <OrdersTable />
                </TabsContent>

                <TabsContent value="proxies">
                    <ProxiesTable />
                </TabsContent>
            </Tabs>
        </div>
    );
};

// --- Sub Components ---

const StatCard = ({ icon, label, value }) => (
    <Card className="p-6 border-gray-200 bg-white shadow-sm">
        <div className="flex items-center gap-4">
            <div className="p-3 bg-gray-50 rounded-lg">{icon}</div>
            <div>
                <p className="text-sm text-gray-500 font-medium">{label}</p>
                <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
            </div>
        </div>
    </Card>
);

const UsersTable = () => {
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState('');

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true });
                setUsers(data);
            } catch (e) { toast.error("Failed to load users"); }
        };
        fetchUsers();
    }, []);

    const filtered = users.filter(u => u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase()));

    const updateSubscription = async (userId, status) => {
        try {
            await axios.put(`${API}/admin/users/${userId}/subscription`, { subscription_status: status }, { withCredentials: true });
            toast.success("Subscription updated");
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, subscription_status: status } : u));
        } catch (e) { toast.error("Failed to update subscription"); }
    };

    return (
        <Card className="p-6 border-gray-200 bg-white">
            <div className="mb-4">
                <div className="relative max-w-sm">
                    <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                    <Input placeholder="Search users..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} />
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 text-gray-600 font-medium">
                        <tr>
                            <th className="px-4 py-3">User</th>
                            <th className="px-4 py-3">Phone / DOB</th>
                            <th className="px-4 py-3">Role</th>
                            <th className="px-4 py-3">Assigned IP</th>
                            <th className="px-4 py-3">Proxy Status</th>
                            <th className="px-4 py-3">Subscription</th>
                            <th className="px-4 py-3">Joined</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filtered.map(u => (
                            <tr key={u.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3">
                                    <div>
                                        <div className="font-semibold text-gray-900">{u.name}</div>
                                        <div className="text-gray-500 text-xs">{u.email}</div>
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="text-gray-900 text-sm">{u.phone || '-'}</div>
                                    <div className="text-gray-400 text-xs">{u.dob || '-'}</div>
                                </td>
                                <td className="px-4 py-3">
                                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                        {u.role}
                                    </span>
                                </td>
                                <td className="px-4 py-3 font-mono text-gray-700">{u.proxy_ip || '-'}</td>
                                <td className="px-4 py-3">
                                    {u.proxy_status === 'assigned' ?
                                        <span className="text-green-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Active</span> :
                                        <span className="text-gray-400">Inactive</span>
                                    }
                                </td>
                                <td className="px-4 py-3 capitalize">
                                    <Select
                                        defaultValue={u.subscription_status}
                                        onValueChange={(val) => updateSubscription(u.id, val)}
                                    >
                                        <SelectTrigger className="w-[110px] h-8">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="active">Active</SelectItem>
                                            <SelectItem value="inactive">Inactive</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </td>
                                <td className="px-4 py-3 text-gray-500">{new Date(u.created_at).toLocaleDateString()}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>
    );
};

const OrdersTable = () => {
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                const { data } = await axios.get(`${API}/admin/orders`, { withCredentials: true });
                setOrders(data);
            } catch (e) { toast.error("Failed to load orders"); }
        };
        fetchOrders();
    }, []);

    // Fetch users for filter
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState('all');
    useEffect(() => {
        const fetchUsers = async () => {
            const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true });
            setUsers(data);
        };
        fetchUsers();
    }, []);

    const filtered = orders.filter(o => selectedUser === 'all' || o.user_id === selectedUser);

    return (
        <Card className="p-6 border-gray-200 bg-white overflow-hidden">
            <div className="flex justify-end mb-4">
                <div className="w-64">
                    <Select value={selectedUser} onValueChange={setSelectedUser}>
                        <SelectTrigger>
                            <SelectValue placeholder="Filter by Client" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Clients</SelectItem>
                            {users.map(u => (
                                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 text-gray-600 font-medium">
                        <tr>
                            <th className="px-4 py-3">Time</th>
                            <th className="px-4 py-3">User ID</th>
                            <th className="px-4 py-3">Broker</th>
                            <th className="px-4 py-3">Symbol</th>
                            <th className="px-4 py-3">Side</th>
                            <th className="px-4 py-3">Qty</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Order ID</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filtered.map(o => (
                            <tr key={o.order_id || Math.random()} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-gray-500">{new Date(o.created_at).toLocaleString()}</td>
                                <td className="px-4 py-3 font-mono text-xs text-gray-400">{o.user_id.substring(0, 8)}...</td>
                                <td className="px-4 py-3 capitalize">{o.broker}</td>
                                <td className="px-4 py-3 font-semibold">{o.details?.symbol}</td>
                                <td className="px-4 py-3">
                                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${o.details?.buy_sell === 'BUY' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                        {o.details?.buy_sell}
                                    </span>
                                </td>
                                <td className="px-4 py-3">{o.details?.qty}</td>
                                <td className="px-4 py-3">
                                    <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                                        {o.status}
                                    </span>
                                </td>
                                <td className="px-4 py-3 font-mono text-xs">{o.order_id}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>
    );
};

const ProxiesTable = () => {
    const [proxies, setProxies] = useState([]);

    useEffect(() => {
        const fetchProxies = async () => {
            try {
                const { data } = await axios.get(`${API}/admin/proxies`, { withCredentials: true });
                setProxies(data);
            } catch (e) { toast.error("Failed to load proxies"); }
        };
        fetchProxies();
    }, []);

    return (
        <Card className="p-6 border-gray-200 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {proxies.map(p => (
                    <div key={p.ip} className="p-4 rounded-xl border border-gray-100 bg-gray-50 flex flex-col gap-2">
                        <div className="flex justify-between items-start">
                            <span className="font-mono font-bold text-gray-800">{p.ip}</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-bold capitalize ${p.status === 'assigned' ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'}`}>
                                {p.status}
                            </span>
                        </div>
                        <div className="text-xs text-gray-500">
                            {p.assigned_user_id ? `Assigned to: ${p.assigned_user_id.substring(0, 8)}...` : 'Idle'}
                        </div>
                        <div className="text-xs text-gray-400 mt-auto pt-2 border-t border-gray-100">
                            Last Active: {new Date(p.last_active_at).toLocaleDateString()}
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );
};

const StrategiesTable = () => {
    const [strategies, setStrategies] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState('all');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [sRes, uRes] = await Promise.all([
                    axios.get(`${API}/admin/strategies`, { withCredentials: true }),
                    axios.get(`${API}/admin/users`, { withCredentials: true })
                ]);
                setStrategies(sRes.data);
                setUsers(uRes.data);
            } catch (e) { toast.error("Failed to load strategies"); }
        };
        fetchData();
    }, []);

    const filtered = strategies.filter(s => selectedUser === 'all' || s.user_id === selectedUser);

    const approveStrategy = async (id) => {
        try {
            await axios.put(`${API}/admin/strategies/${id}/approve`, {}, { withCredentials: true });
            toast.success("Strategy Approved");
            setStrategies(prev => prev.map(s => s.id === id ? { ...s, is_public: true } : s));
        } catch (e) { toast.error("Failed to approve"); }
    };

    return (
        <Card className="p-6 border-gray-200 bg-white">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Strategy Marketplace</h3>
                <div className="w-64">
                    <Select value={selectedUser} onValueChange={setSelectedUser}>
                        <SelectTrigger>
                            <SelectValue placeholder="Filter by Client" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Clients</SelectItem>
                            {users.map(u => (
                                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 text-gray-600 font-medium">
                        <tr>
                            <th className="px-4 py-3">Strategy Name</th>
                            <th className="px-4 py-3">Client</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Deployed?</th>
                            <th className="px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filtered.map(s => (
                            <tr key={s.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 font-semibold">{s.name}</td>
                                <td className="px-4 py-3">
                                    <div className="text-gray-900">{s.user_name}</div>
                                    <div className="text-xs text-gray-500">{s.user_email}</div>
                                </td>
                                <td className="px-4 py-3">
                                    {s.is_public ?
                                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">Public</span> :
                                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-bold">Private</span>
                                    }
                                </td>
                                <td className="px-4 py-3">{s.is_deployed ? 'Yes' : 'No'}</td>
                                <td className="px-4 py-3">
                                    {!s.is_public && (
                                        <Button size="sm" variant="outline" onClick={() => approveStrategy(s.id)}>Approve</Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>
    );
};

export default AdminDashboard;
